import { ErrorHandler, Injectable, inject } from '@angular/core';
import StackTrace from 'stacktrace-js';

import { FrontendLoggerService } from './frontend-logger.service';

/**
 * Globális Angular ErrorHandler – leváltja a beépített konzol-loggert.
 *
 * A `stacktrace-js` library aszinkron source map-et használ, hogy a bundle-ben
 * lévő `main.js:12345` pozíciókat olvasható `auth-core.service.ts:132` formátumra
 * fordítsa. Ezért a stack feldolgozás Promise-alapú.
 */

/** Duck-type interfész az Angular HttpErrorResponse-hoz (import nélkül). */
interface HttpErrorLike {
  status?: number;
  statusText?: string;
  url?: string | null;
  ok?: false;
  error?: unknown;
  message?: string;
  /** stackCaptureInterceptor állítja be: teljes Error objektum, source-map fordításhoz */
  _capturedError?: Error;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly logger = inject(FrontendLoggerService);
  private readonly originalConsoleError = console.error.bind(console);

  constructor() {
    this.overrideConsoleError();
  }

  /** Unhandled kivételek és Promise rejections kezelése. */
  handleError(error: unknown): void {
    this.originalConsoleError('[GlobalErrorHandler]', error);

    if (this.isHttpErrorResponse(error)) {
      const httpErr = error as HttpErrorLike;
      this.handleHttpError(httpErr);
      return;
    }

    const message = this.extractMessage(error);
    if (error instanceof Error) {
      this.translateAndSend('CRITICAL', message, error);
    } else {
      this.logger.sendLog('CRITICAL', message, null);
    }
  }

  /** console.error() hívások felülírása. */
  private overrideConsoleError(): void {
    const original = this.originalConsoleError;
    const self = this;

    console.error = (...args: unknown[]): void => {
      original(...args);

      const httpErr = self.findHttpErrorInArgs(args);
      if (httpErr) {
        self.handleHttpError(httpErr);
        return;
      }

      const message = args
        .map((a) => {
          if (a instanceof Error) return a.message;
          if (typeof a === 'object' && a !== null) {
            try {
              return JSON.stringify(a);
            } catch {
              return String(a);
            }
          }
          return String(a);
        })
        .join(' ');

      const errorArg = args.find((a) => a instanceof Error) as Error | undefined;
      if (errorArg) {
        self.translateAndSend('ERROR', message, errorArg);
      } else {
        self.logger.sendLog('ERROR', message, null);
      }
    };
  }

  /** HttpErrorResponse → message + source-mapped stack → backend log. */
  private handleHttpError(httpErr: HttpErrorLike): void {
    const msg = this.httpErrorMessage(httpErr);
    const errorBody = this.formatErrorBody(httpErr);

    const errorInfo = [
      'HttpErrorResponse {',
      `  status:     ${httpErr.status ?? 0} ${httpErr.statusText ?? ''}`,
      `  url:        ${httpErr.url ?? ''}`,
      `  ok:         ${httpErr.ok ?? false}`,
      `  message:    ${httpErr.message ?? ''}`,
      `  error body: ${errorBody}`,
      '}',
    ].join('\n');

    const captured = httpErr._capturedError;
    const rawStack = captured?.stack ?? '(nincs nyers stack)';

    if (captured) {
      // Source-mapped stack lekérése (aszinkron)
      StackTrace.fromError(captured)
        .then((frames) => {
          const formatted = this.formatFrames(frames);
          const detail = `${errorInfo}\n\n=== Forrás-foldított hívási verem ===\n${formatted}\n\n=== Nyers (bundlelt) verem ===\n${rawStack}`;
          this.logger.sendLog('ERROR', msg, detail);
        })
        .catch((e: unknown) => {
          const errMsg = e instanceof Error ? e.message : String(e);
          const detail = `${errorInfo}\n\n=== Nyers (bundlelt) verem [sourcemap hiba: ${errMsg}] ===\n${rawStack}`;
          this.logger.sendLog('ERROR', msg, detail);
        });
    } else {
      this.logger.sendLog('ERROR', msg, errorInfo);
    }
  }

  /** Error objektum → source-mapped stack → backend log. */
  private translateAndSend(
    level: 'WARNING' | 'ERROR' | 'CRITICAL',
    message: string,
    err: Error,
  ): void {
    const rawStack = err.stack ?? '(nincs nyers stack)';
    StackTrace.fromError(err)
      .then((frames) => {
        const formatted = this.formatFrames(frames);
        const detail = `=== Forrás-foldított hívási verem ===\n${formatted}\n\n=== Nyers (bundlelt) verem ===\n${rawStack}`;
        this.logger.sendLog(level, message, detail);
      })
      .catch((e: unknown) => {
        const errMsg = e instanceof Error ? e.message : String(e);
        const detail = `=== Nyers (bundlelt) verem [sourcemap hiba: ${errMsg}] ===\n${rawStack}`;
        this.logger.sendLog(level, message, detail);
      });
  }

  /** StackFrame[] → olvasható szöveg "at functionName (file:line:col)" formában. */
  private formatFrames(frames: StackTrace.StackFrame[]): string {
    return frames
      .map((f) => {
        const fn = f.functionName ?? '<anonymous>';
        const file = f.fileName ?? '<unknown>';
        const line = f.lineNumber ?? 0;
        const col = f.columnNumber ?? 0;
        return `    at ${fn} (${file}:${line}:${col})`;
      })
      .join('\n');
  }

  private formatErrorBody(err: HttpErrorLike): string {
    try {
      return JSON.stringify(err.error, null, 2);
    } catch {
      return String(err.error);
    }
  }

  private findHttpErrorInArgs(args: unknown[]): HttpErrorLike | null {
    for (const arg of args) {
      if (this.isHttpErrorResponse(arg)) return arg as HttpErrorLike;
    }
    return null;
  }

  private httpErrorMessage(err: HttpErrorLike): string {
    const status = err.status ?? 0;
    const statusText = err.statusText ?? '';
    const url = err.url ?? '';
    return `HTTP ${status} ${statusText} – ${url}`;
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      try {
        return JSON.stringify(error);
      } catch {
        return String(error);
      }
    }
    return String(error);
  }

  private isHttpErrorResponse(value: unknown): boolean {
    return (
      typeof value === 'object' &&
      value !== null &&
      (value as { constructor?: { name?: string } }).constructor?.name === 'HttpErrorResponse'
    );
  }
}

